// Zentrale Formatierung aller Anzeigewerte in deutscher Schreibweise.
// Datum: TT.MM.JJJJ · Zahlen: Dezimalkomma und Tausenderpunkt · Währung: 1.234,56 €
//
// Nicht hierüber formatieren: Werte für <input type="date"> (ISO JJJJ-MM-TT),
// Dateinamen und technische Formate (iCalendar, EPC-QR) – die bleiben normiert.

const LOCALE = 'de-DE';

const waehrungFormatter = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'EUR' });
const datumFormatter = new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' });
const datumZeitFormatter = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});
const datumZeitSekundenFormatter = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
});
const tagMonatFormatter = new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit' });
const zeitFormatter = new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit' });
const monatJahrFormatter = new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric' });
const tagLangFormatter = new Intl.DateTimeFormat(LOCALE, {
  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
});

function toDate(value) {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toNumber(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Datum als TT.MM.JJJJ. */
export function formatDatum(value, fallback = '–') {
  const d = toDate(value);
  return d ? datumFormatter.format(d) : fallback;
}

/**
 * Zeitraum als TT.MM.JJJJ – TT.MM.JJJJ. Liegen beide Daten im selben Jahr, entfällt
 * die Jahresangabe vorne: 01.09. – 30.09.2025. Fehlt eine Grenze: „ab …“ bzw. „bis …“.
 */
export function formatDatumBereich(von, bis, fallback = '–') {
  const a = toDate(von);
  const b = toDate(bis);
  if (!a && !b) return fallback;
  if (!b) return `ab ${datumFormatter.format(a)}`;
  if (!a) return `bis ${datumFormatter.format(b)}`;
  const start = a.getFullYear() === b.getFullYear()
    ? tagMonatFormatter.format(a)
    : datumFormatter.format(a);
  return `${start} – ${datumFormatter.format(b)}`;
}

/** Datum mit Uhrzeit als TT.MM.JJJJ, HH:MM. */
export function formatDatumZeit(value, fallback = '–') {
  const d = toDate(value);
  return d ? datumZeitFormatter.format(d) : fallback;
}

/** Uhrzeit als HH:MM. */
export function formatZeit(value, fallback = '–') {
  const d = toDate(value);
  return d ? zeitFormatter.format(d) : fallback;
}

/**
 * Zeitraum eines Termins. Ohne Uhrzeit (`ganztaegig`) erscheinen nur Datumsangaben,
 * sonst Datum und Uhrzeit; bei einem Ende am selben Tag entfällt dessen Datum:
 *
 *   01.09.2026, 19:00 – 21:00      · Termin mit Uhrzeit an einem Tag
 *   01.09.2026, 19:00              · ohne Endzeit
 *   01.09.2026 – 03.09.2026        · ganztägig über mehrere Tage
 *   01.09.2026 (ganztägig)         · ganztägig an einem Tag
 */
export function formatZeitraum(von, bis, ganztaegig = false, fallback = '–') {
  const a = toDate(von);
  if (!a) return fallback;
  const b = toDate(bis);
  const gleicherTag = b && a.toDateString() === b.toDateString();

  if (ganztaegig) {
    if (!b || gleicherTag) return `${datumFormatter.format(a)} (ganztägig)`;
    return `${datumFormatter.format(a)} – ${datumFormatter.format(b)}`;
  }

  const start = `${datumFormatter.format(a)}, ${zeitFormatter.format(a)}`;
  if (!b) return start;
  if (gleicherTag) return `${start} – ${zeitFormatter.format(b)}`;
  return `${start} – ${datumFormatter.format(b)}, ${zeitFormatter.format(b)}`;
}

/**
 * Datum mit Uhrzeit auf die Sekunde genau (TT.MM.JJJJ, HH:MM:SS).
 * Für Protokolle, in denen die Reihenfolge dicht aufeinanderfolgender
 * Einträge erkennbar bleiben muss.
 */
export function formatDatumZeitSekunden(value, fallback = '–') {
  const d = toDate(value);
  return d ? datumZeitSekundenFormatter.format(d) : fallback;
}

/** Monat und Jahr als „September 2026“ – für Kalenderüberschriften. */
export function formatMonatJahr(value, fallback = '–') {
  const d = toDate(value);
  return d ? monatJahrFormatter.format(d) : fallback;
}

/** Vollständiges Datum mit Wochentag: „Freitag, 4. September 2026“. */
export function formatTagLang(value, fallback = '–') {
  const d = toDate(value);
  return d ? tagLangFormatter.format(d) : fallback;
}

/** Wochentag-Kurzform (Mo, Di, …). */
export function formatWochentag(value, fallback = '') {
  const d = toDate(value);
  return d ? d.toLocaleDateString(LOCALE, { weekday: 'short' }) : fallback;
}

/**
 * Zahl mit Dezimalkomma. `nachkommastellen` erzwingt eine feste Stellenzahl,
 * ohne Angabe wird auf maximal eine Nachkommastelle gekürzt.
 */
export function formatZahl(value, nachkommastellen, fallback = '–') {
  const n = toNumber(value);
  if (n == null) return fallback;
  return n.toLocaleString(LOCALE, nachkommastellen == null
    ? { maximumFractionDigits: 1 }
    : { minimumFractionDigits: nachkommastellen, maximumFractionDigits: nachkommastellen });
}

/** Geldbetrag als 1.234,56 €. */
export function formatWaehrung(value, fallback = '–') {
  const n = toNumber(value);
  return n == null ? fallback : waehrungFormatter.format(n);
}

/** Dateigröße mit deutschem Dezimalkomma. */
export function formatDateiGroesse(bytes) {
  const n = toNumber(bytes);
  if (n == null) return '–';
  if (n < 1024) return `${formatZahl(n, 0)} B`;
  if (n < 1024 * 1024) return `${formatZahl(n / 1024, 1)} KB`;
  return `${formatZahl(n / 1024 / 1024, 1)} MB`;
}
